import {
  generateMarketDefinition,
  generateSearchTerms,
  findCompanies,
  findProducts,
  findCapabilitiesAndFeatures,
  analyzeProductCapabilities,
} from './llm';
import { hashQuery } from '../utils/queryHash';
import { getCachedResearch, saveToCache } from './supabase';

const MAX_COMPANIES = parseInt(import.meta.env.VITE_MAX_COMPANIES || '50', 10);
const MAX_PRODUCTS = parseInt(import.meta.env.VITE_MAX_PRODUCTS || '50', 10);

/**
 * Generate unique ID
 */
function generateId() {
  return Math.random().toString(36).substring(2, 15);
}

/**
 * De-duplicate companies by name (case-insensitive)
 */
function deduplicateCompanies(companies) {
  const seen = new Map();
  return companies.filter(company => {
    const key = company.name.toLowerCase().trim();
    if (seen.has(key)) return false;
    seen.set(key, true);
    return true;
  });
}

/**
 * De-duplicate products by name within a company
 */
function deduplicateProducts(products) {
  const seen = new Map();
  return products.filter(product => {
    const key = product.name.toLowerCase().trim();
    if (seen.has(key)) return false;
    seen.set(key, true);
    return true;
  });
}

/**
 * Main research orchestration function
 * @param {string} userPrompt - The user's market research prompt
 * @param {function} onProgress - Callback for progress updates
 * @returns {Promise<Object>} - The complete research results
 */
export async function conductResearch(userPrompt, onProgress) {
  const foundCompanyNames = new Set();

  const progress = (phase, processed = 0, total = 0, message = '', newCompany = null, warning = null) => {
    onProgress({ phase, processed, total, message, newCompany, warning });
  };

  try {
    // Phase 0: Check cache first
    progress('checking_cache', 0, 1, 'Checking cache...');
    const queryHash = hashQuery(userPrompt);

    const cached = await getCachedResearch(queryHash);
    if (cached) {
      progress('checking_cache', 1, 1, 'Found in cache!');
      progress('completed', 1, 1, 'Loaded from cache!');
      return {
        id: cached.id,
        userPrompt: cached.original_query,
        marketDefinition: cached.market_definition,
        marketName: cached.market_name,
        searchTerms: cached.search_terms,
        companies: cached.companies,
        capabilities: cached.capabilities,
        features: cached.features,
        createdAt: new Date(cached.updated_at),
        fromCache: true
      };
    }
    progress('checking_cache', 1, 1, 'Not cached, running research...');

    // Phase 1: Generate market definition
    progress('generating_definition', 0, 1, 'Analyzing your market...');
    const { marketDefinition, marketName } = await generateMarketDefinition(userPrompt);
    progress('generating_definition', 1, 1, `Market: ${marketName}`);

    // Phase 2: Generate search terms
    progress('generating_search_terms', 0, 1, 'Creating search strategies...');
    const { searchTerms } = await generateSearchTerms(marketDefinition);
    progress('generating_search_terms', 1, 1, `${searchTerms.length} search terms generated`);

    // Phase 3: Find companies for ALL search terms in PARALLEL
    progress('finding_companies', 0, searchTerms.length, 'Discovering companies...');
    const allCompanies = [];
    let completedSearches = 0;

    const onCompanyFound = (companyName) => {
      if (!foundCompanyNames.has(companyName.toLowerCase())) {
        foundCompanyNames.add(companyName.toLowerCase());
        progress('finding_companies', completedSearches, searchTerms.length, `Found: ${companyName}`, companyName);
      }
    };

    const companyPromises = searchTerms.map((term) =>
      findCompanies(term, marketDefinition, onCompanyFound)
        .then(result => {
          completedSearches++;
          progress('finding_companies', completedSearches, searchTerms.length, `Searched: ${term}`);
          return result.companies;
        })
        .catch(err => {
          completedSearches++;
          console.error(`Error searching "${term}":`, err);
          return [];
        })
    );

    const companyResults = await Promise.all(companyPromises);
    companyResults.forEach(companies => allCompanies.push(...companies));

    let uniqueCompanies = deduplicateCompanies(allCompanies).map(c => ({
      ...c,
      id: generateId(),
      products: []
    }));

    const totalCompaniesFound = uniqueCompanies.length;

    if (totalCompaniesFound > MAX_COMPANIES) {
      progress('finding_companies', searchTerms.length, searchTerms.length,
        `Found ${totalCompaniesFound} companies (limited to ${MAX_COMPANIES})`, null,
        `Found ${totalCompaniesFound} companies, limiting to ${MAX_COMPANIES}`);
      uniqueCompanies = uniqueCompanies.slice(0, MAX_COMPANIES);
    } else {
      progress('finding_companies', searchTerms.length, searchTerms.length,
        `Found ${uniqueCompanies.length} unique companies`);
    }

    // Phase 4: Find products for ALL companies in PARALLEL
    progress('finding_products', 0, uniqueCompanies.length, 'Discovering products...');
    let completedProducts = 0;

    const productPromises = uniqueCompanies.map((company) =>
      findProducts(company, marketDefinition)
        .then(result => {
          completedProducts++;
          const products = deduplicateProducts(result.products).map(p => ({
            ...p,
            id: generateId(),
            capabilityIds: [],
            featureIds: []
          }));
          progress('finding_products', completedProducts, uniqueCompanies.length, `${company.name}: ${products.length} product(s)`);
          return {
            companyId: company.id,
            products
          };
        })
        .catch(err => {
          completedProducts++;
          console.error(`Error getting products for ${company.name}:`, err);
          progress('finding_products', completedProducts, uniqueCompanies.length, `Error: ${company.name}`);
          return { companyId: company.id, products: [] };
        })
    );

    const productResults = await Promise.all(productPromises);

    // Attach products to companies
    productResults.forEach(({ companyId, products }) => {
      const company = uniqueCompanies.find(c => c.id === companyId);
      if (company) {
        company.products = products;
      }
    });

    // Filter out companies with no products
    const companiesWithProducts = uniqueCompanies.filter(c => c.products.length > 0);
    let allProducts = companiesWithProducts.flatMap(c => c.products);

    const totalProductsFound = allProducts.length;

    if (totalProductsFound > MAX_PRODUCTS) {
      // Limit products while keeping distribution across companies
      let productsKept = 0;
      for (const company of companiesWithProducts) {
        if (productsKept >= MAX_PRODUCTS) {
          company.products = [];
        } else {
          const canKeep = MAX_PRODUCTS - productsKept;
          if (company.products.length > canKeep) {
            company.products = company.products.slice(0, canKeep);
          }
          productsKept += company.products.length;
        }
      }
      allProducts = companiesWithProducts.flatMap(c => c.products);
      progress('finding_products', uniqueCompanies.length, uniqueCompanies.length,
        `Found ${totalProductsFound} products (limited to ${MAX_PRODUCTS})`, null,
        `Found ${totalProductsFound} products, limiting to ${MAX_PRODUCTS}`);
    } else {
      progress('finding_products', uniqueCompanies.length, uniqueCompanies.length,
        `Found ${allProducts.length} products across ${companiesWithProducts.length} companies`);
    }

    // Phase 5: Find capabilities and features
    progress('finding_capabilities', 0, 1, 'Identifying capabilities and features...');
    const { capabilities: rawCapabilities } = await findCapabilitiesAndFeatures(allProducts, marketDefinition);

    // Assign IDs to capabilities and features
    const capabilities = rawCapabilities.map(cap => ({
      ...cap,
      id: generateId(),
      features: cap.features.map(feat => ({
        ...feat,
        id: generateId()
      }))
    }));

    const features = capabilities.flatMap(c => c.features);
    progress('finding_capabilities', 1, 1,
      `Identified ${capabilities.length} capabilities and ${features.length} features`);

    // Phase 6: Analyze each product's capabilities - ALL in PARALLEL
    const totalAnalysis = companiesWithProducts.reduce((sum, c) => sum + c.products.length, 0);
    progress('analyzing_features', 0, totalAnalysis, 'Analyzing product capabilities...');

    let analyzed = 0;
    const analysisPromises = [];

    for (const company of companiesWithProducts) {
      for (const product of company.products) {
        analysisPromises.push(
          analyzeProductCapabilities(company, product, capabilities, features)
            .then(result => {
              product.capabilityIds = result.supportedCapabilityIds || [];
              product.featureIds = result.supportedFeatureIds || [];
              analyzed++;
              progress('analyzing_features', analyzed, totalAnalysis, `Analyzed: ${product.name}`);
            })
            .catch(err => {
              analyzed++;
              console.error(`Error analyzing ${product.name}:`, err);
              product.capabilityIds = [];
              product.featureIds = [];
              progress('analyzing_features', analyzed, totalAnalysis, `Error: ${product.name}`);
            })
        );
      }
    }

    await Promise.all(analysisPromises);

    progress('completed', totalAnalysis, totalAnalysis, 'Research complete!');

    const result = {
      id: generateId(),
      userPrompt,
      marketDefinition,
      marketName,
      searchTerms,
      companies: companiesWithProducts,
      capabilities,
      features,
      createdAt: new Date(),
      fromCache: false
    };

    // Save to cache (don't await - fire and forget)
    saveToCache(queryHash, userPrompt, result).catch(err => {
      console.error('Failed to save to cache:', err);
    });

    return result;

  } catch (error) {
    progress('error', 0, 0, error.message);
    throw error;
  }
}
